// FilterAST.ts - Updated with proper current state vs expected next

export interface Position {
  start: number;
  end: number;
}

export interface BaseNode {
  type: string;
  position: Position;
}

export interface FilterAST extends BaseNode {
  type: 'Filter';
  expressions: Expression[];
  logicalOperators: LogicalOperator[];
}

export interface Expression extends BaseNode {
  type: 'Expression' | 'GroupExpression';
  field?: FieldNode;
  operator?: OperatorNode;
  value?: ValueNode;
  content?: FilterAST; // for grouped expressions (parentheses)
}

export interface FieldNode extends BaseNode {
  type: 'Field';
  name: string;
  raw: string;
}

export interface OperatorNode extends BaseNode {
  type: 'Operator';
  operator: string;
  raw: string;
}

export interface ValueNode extends BaseNode {
  type: 'Value';
  value: string;
  raw: string;
  isQuoted: boolean;
}

export interface LogicalOperator extends BaseNode {
  type: 'LogicalOperator';
  operator: 'AND' | 'OR';
  raw: string;
}

export interface IncompleteNode extends BaseNode {
  type: 'Incomplete';
  expectedType: 'Field' | 'Operator' | 'Value' | 'LogicalOperator';
  partialText?: string;
}

/** Discriminated union of all concrete AST node types (for type narrowing via node.type) */
export type ASTNode =
  | FieldNode
  | OperatorNode
  | ValueNode
  | LogicalOperator
  | IncompleteNode
  | Expression
  | FilterAST;

// Updated ParseContext with current state vs expected next
export interface ParseContext {
  // What we're currently doing/editing
  currentState:
    | 'TypingField'
    | 'TypingOperator'
    | 'TypingValue'
    | 'TypingLogicalOperator'
    | 'BetweenTokens'
    | 'EditingField'
    | 'EditingOperator'
    | 'EditingValue'
    | 'EditingLogicalOperator';

  // What should come next (for when we finish the current thing)
  expectsNext: 'Field' | 'Operator' | 'Value' | 'LogicalOperator' | 'Nothing';

  // Legacy fields for compatibility
  nodeType:
    | 'Field'
    | 'Operator'
    | 'Value'
    | 'LogicalOperator'
    | 'Expression'
    | 'Unknown';
  parentExpression?: Expression;
  currentNode?: ASTNode;
  isInParentheses: boolean;
  isIncomplete: boolean;

  // Additional context
  partialText?: string; // The partial text being typed
}

// Parser output
export interface ParseResult {
  ast: FilterAST;
  isValid: boolean;
  errors: ParseError[];
  context: ParseContext | null; // Context at cursor position
  incompleteNodes: IncompleteNode[];
  semanticValidation?: {
    isValid: boolean;
    errors: Array<{
      type: 'semantic';
      subtype:
        | 'invalid_field'
        | 'invalid_operator'
        | 'invalid_value'
        | 'invalid_combination';
      message: string;
      field?: string;
      operator?: string;
      value?: string;
      suggestions?: string[];
      position: { start: number; end: number };
    }>;
    warnings: Array<{
      type: 'warning';
      message: string;
      position: { start: number; end: number };
    }>;
  };
}

export interface ParseError {
  message: string;
  position: Position;
  severity: 'error' | 'warning';
  expectedTokens?: string[];
  actualToken?: string;
}

// Visitor pattern for AST traversal
export interface ASTVisitor<T> {
  visitFilter(node: FilterAST): T;
  visitExpression(node: Expression): T;
  visitField(node: FieldNode): T;
  visitOperator(node: OperatorNode): T;
  visitValue(node: ValueNode): T;
  visitLogicalOperator(node: LogicalOperator): T;
  visitIncomplete(node: IncompleteNode): T;
}

// Helper functions for AST manipulation
export class ASTUtils {
  static findNodeAtPosition(ast: FilterAST, position: number): BaseNode | null {
    return this.traverse(ast, (node) => {
      const inNode =
        position >= node.position.start && position <= node.position.end;

      if (inNode) {
        return node;
      }
      return null;
    });
  }

  static findExpressionAtPosition(
    ast: FilterAST,
    position: number,
  ): Expression | null {
    for (const expr of ast.expressions) {
      if (position >= expr.position.start && position <= expr.position.end) {
        if (expr.type === 'GroupExpression' && expr.content) {
          // Recursively search in grouped expressions
          const nested = this.findExpressionAtPosition(expr.content, position);
          return nested || expr;
        }
        return expr;
      }
    }
    return null;
  }

  static getIncompleteNodeAtPosition(
    ast: FilterAST,
    position: number,
  ): IncompleteNode | null {
    return this.traverse(ast, (node) => {
      if (
        node.type === 'Incomplete' &&
        position >= node.position.start &&
        position <= node.position.end
      ) {
        return node as IncompleteNode;
      }
      return null;
    });
  }

  private static traverse<T>(
    node: BaseNode,
    visitor: (node: BaseNode) => T | null,
  ): T | null {
    const result = visitor(node);
    if (result !== null) return result;

    switch (node.type) {
      case 'Filter':
        const filter = node as FilterAST;
        for (const expr of filter.expressions) {
          const result = this.traverse(expr, visitor);
          if (result !== null) return result;
        }
        for (const logOp of filter.logicalOperators) {
          const result = this.traverse(logOp, visitor);
          if (result !== null) return result;
        }
        break;

      case 'Expression':
      case 'GroupExpression':
        const expr = node as Expression;
        if (expr.field) {
          const result = this.traverse(expr.field, visitor);
          if (result !== null) return result;
        }
        if (expr.operator) {
          const result = this.traverse(expr.operator, visitor);
          if (result !== null) return result;
        }
        if (expr.value) {
          const result = this.traverse(expr.value, visitor);
          if (result !== null) return result;
        }
        if (expr.content) {
          const result = this.traverse(expr.content, visitor);
          if (result !== null) return result;
        }
        break;
    }

    return null;
  }

  static isExpressionComplete(
    expr: Expression,
    isBooleanField: (name: string) => boolean,
  ): boolean {
    if (expr.type === 'GroupExpression') {
      return expr.content
        ? this.isFilterComplete(expr.content, isBooleanField)
        : false;
    }

    // For regular expressions, we need at least a field
    if (!expr.field) return false;

    // For boolean fields, just having the field is enough
    if (isBooleanField(expr.field.name)) {
      return true;
    }

    // For other fields, we need operator and value
    return !!(expr.operator && expr.value);
  }

  static isFilterComplete(
    filter: FilterAST,
    isBooleanField: (name: string) => boolean,
  ): boolean {
    if (filter.expressions.length === 0) return false;

    // Check if all expressions are complete
    const allExpressionsComplete = filter.expressions.every((expr) =>
      this.isExpressionComplete(expr, isBooleanField),
    );

    // Check if we have the right number of logical operators
    const expectedLogicalOps = filter.expressions.length - 1;
    const hasCorrectLogicalOps =
      filter.logicalOperators.length === expectedLogicalOps;

    return allExpressionsComplete && hasCorrectLogicalOps;
  }

  static getLastExpression(filter: FilterAST): Expression | null {
    return filter.expressions[filter.expressions.length - 1] || null;
  }

  static getLastLogicalOperator(filter: FilterAST): LogicalOperator | null {
    return filter.logicalOperators[filter.logicalOperators.length - 1] || null;
  }
}
